import {
  DragHandleLocation,
  LAYOUT_COLUMN_TOTAL,
  LAYOUT_ROW_HEIGHT,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import { BaseModule, ModulePosition } from 'dashboard/lib/module';

export const normalizePixelCoordinateFrame = (x: number, y: number, boundingRect: DOMRect) => {
  const normalizedX = x - boundingRect.x;
  const normalizedY = y - boundingRect.y;

  return { x: normalizedX, y: normalizedY };
};

export const getPixelColumnWidth = (containerWidth: number) => {
  return containerWidth / LAYOUT_COLUMN_TOTAL;
};

export const clampGridCoordinates = (module: ModulePosition, totalRows: number) => {
  const { x, y, width, height } = module;

  const clampedX = Math.min(Math.max(x, 0), LAYOUT_COLUMN_TOTAL - width);
  const clampedY = Math.min(Math.max(y, 0), totalRows - height);

  const clampedGridCoords = {
    width,
    height,
    x: clampedX,
    y: clampedY,
  };

  return clampedGridCoords;
};

export const clampGridSize = (module: ModulePosition, totalRows: number) => {
  const { x, y, width, height } = module;

  let clampedWidth = width;
  let clampedHeight = height;

  if (x + width > LAYOUT_COLUMN_TOTAL) {
    clampedWidth = LAYOUT_COLUMN_TOTAL - x;
  } else if (x < 0) {
    clampedWidth = width + x;
  } else {
    clampedWidth = Math.max(width, 1);
  }

  if (y + height > totalRows) {
    clampedHeight = totalRows - y;
  } else if (y < 0) {
    clampedHeight = height + y;
  } else {
    // The minimum height of 6 should match the minimum height of our smallest module
    // which for now is button. In the future we could set a minimum height on each
    // module.
    clampedHeight = Math.max(height, 6);
  }

  const clampedX = Math.min(Math.max(x, 0), LAYOUT_COLUMN_TOTAL - clampedWidth);
  const clampedY = Math.min(Math.max(y, 0), totalRows - clampedHeight);

  const clampedGridCoords = {
    width: clampedWidth,
    height: clampedHeight,
    x: clampedX,
    y: clampedY,
  };

  return clampedGridCoords;
};

/**
 * The following functions are shared between ModuleDragPreview and LayoutCanvas to keep preview and final result consistent.
 */
export const repositionModule = (
  x: number,
  y: number,
  modulePosition: ModulePosition,
  boundingRect: DOMRect,
  totalRows: number,
) => {
  const normalizedCoordinates = normalizePixelCoordinateFrame(x, y, boundingRect);

  // column width is responsive so we have to calculate it
  const columnWidth = getPixelColumnWidth(boundingRect.width);

  const columnNumber = Math.round(normalizedCoordinates.x / columnWidth);
  const rowNumber = Math.round(normalizedCoordinates.y / LAYOUT_ROW_HEIGHT);

  const clampedPosition = clampGridCoordinates(
    {
      x: columnNumber,
      y: rowNumber,
      width: modulePosition.width,
      height: modulePosition.height,
    },
    totalRows,
  );

  return clampedPosition;
};

export const resizeModule = (
  deltaX: number,
  deltaY: number,
  handleLocation: DragHandleLocation,
  modulePosition: ModulePosition,
  boundingRect: DOMRect,
  totalRows: number,
) => {
  // column width is responsive so we have to calculate it
  const columnWidth = getPixelColumnWidth(boundingRect.width);

  // calculate absolute deltas
  const columnDelta = Math.round(deltaX / columnWidth);
  const rowDelta = Math.round(deltaY / LAYOUT_ROW_HEIGHT);

  let columnNumber = modulePosition.x;
  let rowNumber = modulePosition.y;
  let width = modulePosition.width;
  let height = modulePosition.height;

  if (
    handleLocation === DragHandleLocation.TopLeft ||
    handleLocation === DragHandleLocation.TopRight
  ) {
    rowNumber = modulePosition.y + rowDelta;
    height = modulePosition.height - rowDelta;
  } else {
    height = modulePosition.height + rowDelta;
  }

  if (
    handleLocation === DragHandleLocation.TopLeft ||
    handleLocation === DragHandleLocation.BottomLeft
  ) {
    columnNumber = modulePosition.x + columnDelta;
    width = modulePosition.width - columnDelta;
  } else {
    width = modulePosition.width + columnDelta;
  }

  const clampedPosition = clampGridSize(
    {
      x: columnNumber,
      y: rowNumber,
      width,
      height,
    },
    totalRows,
  );

  return clampedPosition;
};

/**
 * Sorts module by their y position, then x position.
 * @param modules list of modules to sort
 * @returns sorted list of modules
 */
export function sortModules<T extends BaseModule>(modules: T[]): T[] {
  // Shallow clone to prevent the original array from being re-ordered, since re-ordering
  // is handled as a delete + insert by browsers which breaks CSS transitions.
  const sortedModules = [...modules];
  // sort modules by their y positions
  return sortedModules.sort((a, b) => {
    if (a.position.y === b.position.y) {
      // tiebreak by x position
      return a.position.x - b.position.x;
    }
    return a.position.y - b.position.y;
  });
}

/**
 * Resolves collisions around the priotized module, mutates the original module array.
 */
export const resolveCollisions = (
  modules: BaseModule[],
  priorityModuleToken: string, // this is usually the selected module because we don't want to move the selected module!
) => {
  const priorityModule = modules.find((m) => m.token === priorityModuleToken);
  if (!priorityModule) {
    throw new Error('priorityModuleToken does not match any modules in the provided modules list');
  }

  const priorityModulePosition = priorityModule.position;

  // x coordinates of the module right above the current module being shifted down
  let minX = priorityModulePosition.x;
  let maxX = priorityModulePosition.x + priorityModulePosition.width;

  // sort modules from top down by y coordinates
  const sortedModules = sortModules(modules);

  sortedModules.forEach((m: BaseModule) => {
    if (m.token === priorityModuleToken) return;
    if (priorityModulePosition) {
      if (
        m.position.y >= priorityModulePosition.y &&
        !(m.position.x + m.position.width < minX || m.position.x > maxX)
      ) {
        m.position.y = m.position.y + priorityModulePosition.height;
        minX = m.position.x;
        maxX = m.position.x + m.position.width;
      }
    }
  });
};

/**
 * Top compacts modules, mutates the original module array.
 */
export const performTopCompaction = (modules: BaseModule[]) => {
  const sortedModules = sortModules(modules);

  // keep track of the max height in each row
  const columnHeights = Array(LAYOUT_COLUMN_TOTAL).fill(0);

  sortedModules.forEach((m) => {
    const { x, width, height } = m.position;

    // find max height of the column the module would rest on
    const maxHeight = Math.max(...columnHeights.slice(x, x + width));

    // set module's y position to the max height
    m.position.y = maxHeight;

    // update column heights with new module
    for (let i = x; i < x + width; i++) {
      columnHeights[i] = maxHeight + height;
    }
  });
};
